import { MoreOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Badge } from "antd";
import styled from "styled-components";

import { SecondaryText } from "@/components/Typography/Styled";

// Layout
export const StyledScrollContent = styled.div`
    overflow-y: auto;
    padding-bottom: 20px;
`;

// Team Stats
export const StyledTeamStats = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 16px;
    padding: 16px;
    background-color: ${(props) => props.theme.secondaryBackgroundColor};
    border-radius: 12px;
    border: 1px solid ${(props) => props.theme.secondaryBorderColor};
`;

export const StyledTeamStatsItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .ant-badge {
        .ant-scroll-number {
            font-size: 14px;
            font-weight: 600;
            min-width: 28px;
            height: 28px;
            line-height: 28px;
            border-radius: 14px;
        }
    }
`;

// Search Input
export const StyledInputWrapper = styled.div`
    margin-bottom: 16px;
    width: 100%;

    .ant-input::placeholder {
        color: ${(props) => props.theme.shelfValueColor};
    }

    .ant-input-wrapper,
    input {
        height: 44px;
    }

    input {
        font-weight: 400;
        font-size: 14px;
        line-height: 120%;
        background-color: ${(props) => props.theme.secondaryBackgroundColor};
        border-color: ${(props) => props.theme.secondaryBorderColor};

        &:focus {
            border-color: ${(props) => props.theme.itemBorder};
            box-shadow: 0 0 0 2px ${(props) => props.theme.itemBorder}15;
        }
    }

    .ant-input-group-addon {
        background-color: ${(props) => props.theme.collapseHeaderBackground};
        border-color: ${(props) => props.theme.secondaryBorderColor};

        svg {
            fill: ${(props) => props.theme.secondaryTypographyColor} !important;
        }
    }

    .ant-input-clear-icon {
        color: ${(props) => props.theme.secondaryTypographyColor};
    }
`;

// Team Header
export const StyledTeamHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
    padding: 12px;
    background-color: ${(props) => props.theme.itemBackground};
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.secondaryBorderColor};

    ${SecondaryText} {
        font-size: 12px;

        .anticon {
            margin-right: 4px;
        }
    }
`;

// Section Titles
export const StyledSectionTitle = styled(SecondaryText)`
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
    margin-top: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-of-type {
        margin-top: 8px;
    }
`;

// Active User Section
export const StyledActiveUserSection = styled.div`
    margin-bottom: 16px;
`;

// Member Card
export const StyledMemberCard = styled.div<{ isActive?: boolean }>`
    border-radius: 12px;
    margin-bottom: 12px;
    display: flex;
    padding: 14px;
    gap: 12px;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;

    background-color: ${(props) => props.theme.secondaryBackgroundColor};
    border: 2px solid
        ${(props) =>
            props.isActive
                ? props.theme.itemBorder
                : props.theme.secondaryBorderColor};
    box-shadow: ${(props) =>
        props.isActive ? `0 4px 12px ${props.theme.itemBorder}20` : "none"};

    &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        background-color: ${(props) =>
            props.isActive ? props.theme.itemBorder : "transparent"};
        transition: all 0.2s ease;
    }
`;

// Member Info
export const StyledMemberInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

export const StyledWorkStatus = styled(SecondaryText)`
    font-size: 13px;

    .anticon {
        margin-right: 4px;

        &.anticon-check-circle {
            color: #388e3c;
        }

        &.anticon-clock-circle {
            color: #d32f2f;
        }
    }
`;

export const StyledPhoneLink = styled.a`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${(props) => props.theme.additionalTypographyColor};
    text-decoration: none;
    margin-top: 2px;
    transition: all 0.2s ease;
    font-weight: 500;

    .anticon {
        font-size: 13px;
    }

    &:hover {
        color: ${(props) => props.theme.itemBorder};
        text-decoration: underline;
    }
`;

// Member Badge
export const StyledTeamBadge = styled(Badge)<{ isWorking: boolean }>`
    .ant-badge-dot {
        top: 38px;
        left: 32px;
        height: 12px;
        width: 12px;
        border: 2px solid ${(props) => props.theme.secondaryBackgroundColor};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

        background-color: ${({ isWorking, theme }) =>
            isWorking
                ? theme.successTypographyColor
                : theme.dangerTypographyColor};
    }
`;

export const StyledTeamLogo = styled(UserOutlined)`
    color: ${(props) => props.theme.primaryTypographyColor};
    font-size: 20px;
`;

// Action Button
export const StyledActionButton = styled(Avatar)<{
    hasSchedule?: boolean;
    disabled?: boolean;
}>`
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    color: ${(props) => props.theme.colors.white};
    background-color: ${(props) => {
        if (props.disabled) return props.theme.disabledTypographyColor;
        if (props.hasSchedule) return props.theme.successTypographyColor;
        return props.theme.itemBorder;
    }};
    transition: all 0.2s ease;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    flex-shrink: 0;
    box-shadow: ${(props) =>
        props.disabled ? "none" : "0 2px 8px rgba(0, 0, 0, 0.15)"};
    border: 2px solid
        ${(props) => {
            if (props.disabled) return "transparent";
            if (props.hasSchedule) return props.theme.successTypographyColor;
            return props.theme.itemBorder;
        }};

    .anticon {
        font-size: 18px;
    }
`;

// No Results
export const StyledNoResults = styled(SecondaryText)`
    text-align: center;
    margin-top: 20px;
`;

export const StyledMoreOutlined = styled(MoreOutlined)`
    color: ${({ theme }) => theme.primaryTypographyColor};
    font-size: 24px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: ${({ theme }) => theme.itemBorder};
    }
`;
